import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Anchor, Package, Globe, Ship, Award, Info, Mail } from 'lucide-react';
import logo from '../../assets/images/logo.png';

const isNavItemActiveForLocation = (pathname, hash, link) => {
  const basePath = link.to.split('#')[0];
  if (basePath === '/services') {
    if (link.name === 'Ship Chandlers') {
      return pathname === '/services' && hash === '#chandlery';
    }
    if (link.name === 'Our Services') {
      return pathname === '/services' && hash !== '#chandlery';
    }
  }
  return pathname === basePath || pathname === link.to;
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', to: '/', icon: <Anchor size={17} strokeWidth={2} /> },
    { name: 'Our Services', to: '/services', icon: <Package size={17} strokeWidth={2} /> },
    { name: 'Import | Export', to: '/import-export', icon: <Globe size={17} strokeWidth={2} /> },
    { name: 'Ship Chandlers', to: '/services#chandlery', icon: <Ship size={17} strokeWidth={2} /> },
    { name: 'Certifications', to: '/certifications', icon: <Award size={17} strokeWidth={2} /> },
    { name: 'About Us', to: '/about', icon: <Info size={17} strokeWidth={2} /> },
    { name: 'Contact Us', to: '/contact', icon: <Mail size={17} strokeWidth={2} /> },
  ];

  const handleLinkClick = () => setIsMenuOpen(false);

  // Check if header should be transparent (only on desktop/tablet, not mobile)
  const shouldBeTransparent = isHomePage && !isScrolled;

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 transition-all duration-300 z-50 ${
          shouldBeTransparent 
            ? 'lg:bg-transparent md:bg-transparent bg-white' 
            : 'bg-white shadow-md'
        }`}
      >
        <div className="container-custom">
          <div className="flex justify-between items-center py-3.5 md:py-4">
            {/* Logo */}
            <Link to="/" className="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal">
              <img 
                src={logo} 
                alt="Infinity Enterprises" 
                className="h-11 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navLinks.map((link) => {
                const active = isNavItemActiveForLocation(location.pathname, location.hash, link);
                return (
                  <NavLink
                    key={link.name}
                    to={link.to}
                    className={() => {
                      let textColor = shouldBeTransparent ? 'text-white/95' : 'text-darkGray';
                      if (active) textColor = 'text-teal';
                      return `flex items-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium transition-colors duration-200 ${textColor} hover:text-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal`;
                    }}
                  >
                    <span className={`opacity-90 ${active ? 'text-teal' : ''}`} aria-hidden>
                      {link.icon}
                    </span>
                    <span>{link.name}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg transition-colors z-50 relative text-navy hover:bg-lightGray"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer */}
      <div className="h-[73px]"></div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
            onClick={() => setIsMenuOpen(false)} 
          />
          <div className="fixed top-[73px] left-0 right-0 bg-white shadow-lg z-40 lg:hidden max-h-[calc(100vh-73px)] overflow-y-auto animate-slide-down">
            <nav className="flex flex-col py-4">
              {navLinks.map((link) => {
                const active = isNavItemActiveForLocation(location.pathname, location.hash, link);
                return (
                  <NavLink
                    key={link.name}
                    to={link.to}
                    className={() =>
                      `flex items-center gap-3 py-3 px-6 transition-colors ${
                        active
                          ? 'bg-lightGray text-teal border-l-4 border-teal'
                          : 'text-darkGray hover:bg-lightGray hover:text-teal border-l-4 border-transparent'
                      }`
                    }
                    onClick={handleLinkClick}
                  >
                    <span className="text-teal">{link.icon}</span>
                    <span className="font-medium">{link.name}</span>
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Header;